import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

function formatSections(sections) {
  return JSON.stringify(Array.isArray(sections) ? sections : [], null, 2);
}

export default function LandingSectionsEditor({ sections, onChange, onValidityChange }) {
  const [rawValue, setRawValue] = useState(formatSections(sections));
  const [error, setError] = useState("");

  useEffect(() => {
    setRawValue(formatSections(sections));
  }, [sections]);

  const introSectionIndex = useMemo(
    () => sections.findIndex((section) => section?.type === "intro"),
    [sections]
  );

  const introSection = introSectionIndex >= 0 ? sections[introSectionIndex] : null;

  const updateIntroField = (field, value) => {
    const nextSections = sections.map((section, index) => {
      if (index !== introSectionIndex) return section;
      return { ...section, [field]: value };
    });

    setError("");
    onValidityChange(true);
    onChange(nextSections);
  };

  const handleRawChange = (value) => {
    setRawValue(value);

    try {
      const parsed = JSON.parse(value || "[]");

      if (!Array.isArray(parsed)) {
        throw new Error("Landing-Sections müssen ein Array sein.");
      }

      setError("");
      onValidityChange(true);
      onChange(parsed);
    } catch (err) {
      setError(err.message || "Ungültiges JSON");
      onValidityChange(false);
    }
  };

  const formatCurrentJson = () => {
    const formatted = formatSections(sections);
    setRawValue(formatted);
    setError("");
    onValidityChange(true);
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-6" data-testid="landing-sections-editor">
      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <Label className="text-slate-700 font-semibold text-base">Landingpage-Inhalte</Label>
            <p className="text-sm text-slate-500 mt-1">
              Hier bearbeitest du die echten Texte der Produktseite. Der Tina-/Alleinerziehend-Text liegt im Intro-Block.
            </p>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={formatCurrentJson} data-testid="format-landing-sections-json">
            JSON formatieren
          </Button>
        </div>

        <div className="flex flex-wrap gap-2" data-testid="landing-sections-summary">
          {sections.length > 0 ? (
            sections.map((section, index) => (
              <Badge key={`${section.type || "section"}-${index}`} variant="secondary" className="bg-slate-100 text-slate-700 border-0">
                {section.type || `section-${index + 1}`}
              </Badge>
            ))
          ) : (
            <Badge variant="secondary" className="bg-amber-100 text-amber-700 border-0">
              Keine Landing-Sections vorhanden
            </Badge>
          )}
        </div>
      </div>

      {introSection && (
        <div className="rounded-2xl border border-[#ff0090]/15 bg-[#ff0090]/[0.03] p-5 space-y-4" data-testid="intro-quick-editor">
          <div>
            <h3 className="font-['Outfit'] text-lg font-bold text-slate-900">Schnellbearbeitung: Intro/Tina-Block</h3>
            <p className="text-sm text-slate-500 mt-1">Diese Felder werden direkt in der Landingpage angezeigt.</p>
          </div>

          <div>
            <Label htmlFor="intro-title-input" className="text-slate-700 font-semibold">Überschrift</Label>
            <Input
              id="intro-title-input"
              value={introSection.title || ""}
              onChange={(event) => updateIntroField("title", event.target.value)}
              className="mt-2 rounded-xl"
              data-testid="intro-title-input"
            />
          </div>

          <div>
            <Label htmlFor="intro-description-input" className="text-slate-700 font-semibold">Text</Label>
            <Textarea
              id="intro-description-input"
              value={introSection.description || ""}
              onChange={(event) => updateIntroField("description", event.target.value)}
              rows={6}
              className="mt-2 rounded-xl"
              data-testid="intro-description-input"
            />
          </div>

          <div>
            <Label htmlFor="intro-name-input" className="text-slate-700 font-semibold">Name</Label>
            <Input
              id="intro-name-input"
              value={introSection.name || ""}
              onChange={(event) => updateIntroField("name", event.target.value)}
              className="mt-2 rounded-xl"
              data-testid="intro-name-input"
            />
          </div>
        </div>
      )}

      <div>
        <Label htmlFor="landing-sections-json" className="text-slate-700 font-semibold">Alle Sections als JSON</Label>
        <Textarea
          id="landing-sections-json"
          value={rawValue}
          onChange={(event) => handleRawChange(event.target.value)}
          rows={18}
          className="mt-2 rounded-xl font-mono text-xs"
          data-testid="landing-sections-json-input"
        />
        {error ? (
          <p className="text-sm text-red-500 mt-2" data-testid="landing-sections-json-error">{error}</p>
        ) : (
          <p className="text-xs text-slate-400 mt-2" data-testid="landing-sections-json-help">
            Änderungen hier werden beim Speichern des Produkts dauerhaft übernommen.
          </p>
        )}
      </div>
    </div>
  );
}